import {
    Box,
    IconButton,
    Slider,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    TooltipProps,
    Typography,
} from '@mui/material';
import { Contrast } from 'lucide-react';
import {
    ComponentType,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { enqueueSnackbar } from '~/utils/notifications';
import ColorSwatchPicker from '~/components/Inputs/Color/ColorSwatchPicker';
import { StructureSetId } from '~/domain/changes/ids';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import RegionResetButton from '../RegionResetButton';
import { WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS } from '../autoPreview';
import {
    STAGE_ROW_TOOLTIP_PLACEMENT,
    SmoothingToggleRow,
    ToggleNumberRow,
    ValueRow,
} from '~/components/toolbar/shared/StageToggleRows';
import { useParkedContouringTool } from '~/components/toolbar/shared/useParkedContouringTool';
import { useVoiRegionRestriction } from '~/components/toolbar/shared/useVoiRegionRestriction';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { findExternalRoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { generateUniqueStructureNameFromList } from '~/domain/services/dicomProcessor';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useReviewWindowLevel } from '~/hooks/useReviewWindowLevel';
import {
    cm2ToMm2,
    cmToMm,
    formatMm3AsCm3,
    mm2ToCm2,
    mmToCm,
} from '~/utils/units';
import { validateStructureName } from '~/utils/validation';
import {
    ActionButtons,
    getStructureColor,
    outputTargetBlocker,
    Label,
    OutputStructureSelect,
    StructureOption,
} from '../OperationFormControls';
import {
    NEW_OUTPUT_TARGET,
    OutputTarget,
    overwriteTarget,
    overwrittenRoiNumber,
} from '~/hooks/structure-set/outputTarget';
import {
    BodyContourMaskCache,
    BodyContourParams,
    bodyContourPreviewKey,
    roiHasEngineGeometry,
} from './bodyContourParams';
import {
    OwnOutputResolution,
    resolveOwnOutputByName,
    resolveThresholdRange,
    stageEnabled,
    THRESHOLD_SEGMENTATION_PRESET_ORDER,
    THRESHOLD_SEGMENTATION_PRESETS,
    ThresholdSegmentationPreset,
    ThresholdSegmentationPresetId,
} from './thresholdSegmentationPresets';
import { useRoiSdfVersions } from '~/hooks/structure-set/useRoiSdfVersions';
import { useBodyContourApplication } from './useBodyContourApplication';
import { useBodyContourPreview } from './useBodyContourPreview';
import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { useReportBusy } from '~/components/toolbar/OperationsToolbar/useReportBusy';

/** Per-tab (preset) parameter state. Each tab keeps its own copy for the
 *  panel session, seeded from the preset, so switching tabs restores the
 *  user's values for that tab. */
interface TabParams {
    thresholdHu: number;
    /**
     * Inclusive UPPER HU bound of a BANDED preset's threshold (Bone),
     * edited with the second handle of the threshold slider. Undefined
     * for the single-sided presets, which include everything at or above
     * the threshold — the preset decides, never this state (see the
     * params memo).
     */
    upperThresholdHu: number | undefined;
    minPartSizeEnabled: boolean;
    minPartAreaMm2: number;
    fillHolesEnabled: boolean;
    smoothingEnabled: boolean;
    /** Continuous strength position (1..MAX_SMOOTHING_LEVEL, 0.1-quantized
     *  by the slider), not a level index. */
    smoothingStrength: number;
    keepLargest2dEnabled: boolean;
    keepLargest2dCount: number;
    keepLargest3dEnabled: boolean;
    keepLargest3dCount: number;
    marginMm: number;
    color: DicomColor;
    customRoiName: string;
    /**
     * Chosen output for the by-name presets (Bone / High density): an
     * existing structure, or "New Structure" (then {@link customRoiName}
     * names it). Seeded once from
     * the preset's default name — see the output-seeding effect. The
     * Body preset never uses it: its select's value is DERIVED from the
     * structure set's one EXTERNAL (or New Structure when none exists),
     * never stored.
     */
    outputTarget: OutputTarget;
}

function initTabParams(preset: ThresholdSegmentationPreset): TabParams {
    return {
        thresholdHu: preset.threshold.defaultHu,
        upperThresholdHu: preset.threshold.upper?.defaultHu,
        minPartSizeEnabled: preset.minPartSize.defaultEnabled,
        minPartAreaMm2: preset.minPartSize.defaultAreaMm2,
        fillHolesEnabled: preset.fillHoles2d.defaultEnabled,
        smoothingEnabled: preset.smoothing.defaultEnabled,
        smoothingStrength: preset.smoothing.defaultStrength,
        keepLargest2dEnabled: preset.keepLargest2d.defaultEnabled,
        keepLargest2dCount: preset.keepLargest2d.defaultCount,
        keepLargest3dEnabled: preset.keepLargest3d.defaultEnabled,
        keepLargest3dCount: preset.keepLargest3d.defaultCount,
        marginMm: preset.margin.exposed ? preset.margin.defaultMm : 0,
        color: { ...preset.output.color },
        customRoiName: preset.output.name,
        outputTarget: NEW_OUTPUT_TARGET,
    };
}

/** Build a per-preset record — the one place that enumerates the ids, so
 *  adding a preset stays a registry-only edit. */
function mapPresets<T>(
    build: (preset: ThresholdSegmentationPreset) => T
): Record<ThresholdSegmentationPresetId, T> {
    return Object.fromEntries(
        THRESHOLD_SEGMENTATION_PRESET_ORDER.map((id) => [
            id,
            build(THRESHOLD_SEGMENTATION_PRESETS[id]),
        ])
    ) as Record<ThresholdSegmentationPresetId, T>;
}

function initAllTabs(): Record<ThresholdSegmentationPresetId, TabParams> {
    return mapPresets(initTabParams);
}

/**
 * Input-structure sentinel for the shared {@link OutputStructureSelect}.
 * Threshold segmentation reads the IMAGE, not a source ROI, so no
 * structure may render as the "(Current)" option — a roiNumber no
 * structure can carry keeps that branch dark without a second control.
 */
const NO_INPUT_STRUCTURE = -1;

/**
 * Selection sink for the Body preset's output select, whose value is
 * DERIVED from the read model rather than stored: the domain's
 * single-EXTERNAL rule leaves at most one option in the control, so the
 * only possible change event re-selects the current value — there is no
 * state to write, and a stored copy could only ever go stale (EXTERNAL
 * deleted mid-session, primary image switched).
 */
const NOOP_SET_BODY_OUTPUT = () => {};

/** Why the by-name output refuses to write, or null when it can. */
function ownOutputRefusal(
    intl: IntlShape,
    resolution: OwnOutputResolution
): string | null {
    switch (resolution.kind) {
        case 'refuse-approved':
            return intl.formatMessage(
                { id: 'thresholdSeg.nameApproved' },
                { name: resolution.name }
            );
        case 'refuse-ambiguous':
            return intl.formatMessage(
                { id: 'thresholdSeg.nameAmbiguous' },
                { name: resolution.name }
            );
        case 'create':
        case 'overwrite':
            return null;
    }
}

/** The negative margins cancel the label's mb and most of the small
 *  slider's built-in padding, which otherwise read as a detached
 *  control. Shared by both threshold slider variants so the banded and
 *  single-sided tabs keep the identical vertical rhythm. Tuned against
 *  the BARE label, so the review-W/L toggle sharing the label's row must
 *  not make that row taller — see the row's own comment. */
const THRESHOLD_SLIDER_SX = { mt: -1.75, mb: -0.5 } as const;

/**
 * The preset tabs' tooltip wrapper. {@link Tabs} identifies each tab by
 * the `value` prop of its DIRECT child — here the Tooltip — and clones
 * the selection props onto it; the Tooltip forwards every prop it does
 * not consume to the Tab it wraps. Its public type does not declare
 * `value` (it declares no forwarded props at all), so the one prop Tabs
 * reads back off the element is declared here rather than cast away at
 * the call site.
 */
const PresetTabTooltip = Tooltip as ComponentType<
    TooltipProps & { value: ThresholdSegmentationPresetId }
>;

interface BodyContourOperationContentProps {
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

/**
 * Threshold Segmentation panel — SR-WOR-H-27-2 (Body preset specifics:
 * SR-WOR-H-27-1). One pipeline, three data presets (Body | Bone | High
 * density) rendered as tabs; rows appear in pipeline order on every tab.
 *
 * Output semantics differ per preset. Body: the domain allows one
 * EXTERNAL per structure set, so the output select is locked to the
 * existing EXTERNAL (its only option — contours replaced on apply) or,
 * when none exists, to a new EXTERNAL via New Structure. Bone / High
 * density: the output is resolved BY NAME — an unapproved structure with
 * the chosen name is overwritten (re-apply replaces what this tool
 * created), an approved one refuses with the reason surfaced.
 *
 * The preview follows the parameters automatically, so the viewports
 * always show what applying would commit. A change aborts the run in
 * flight and schedules a replacement, with a debounce that coalesces a
 * burst — a generation is a multi-second compute on real CTs, so the
 * displayed preview stays up until its replacement is ready rather than
 * blanking. A TAB SWITCH clears the held preview outright: that result
 * belongs to another preset's structure entirely.
 */
export default function BodyContourOperationContent({
    onClose,
    onPreventCloseChange,
}: BodyContourOperationContentProps) {
    const intl = useIntl();
    const { environment: activeEnvironment, structureSet: readModel } =
        useActiveEditTarget();
    // The resolver yields `undefined`; the consumers below take `null`.
    // Normalise once at the boundary.
    const environment = activeEnvironment ?? null;
    const image = activeEnvironment?.image?.parent;

    const [presetId, setPresetId] =
        useState<ThresholdSegmentationPresetId>('body');
    const preset = THRESHOLD_SEGMENTATION_PRESETS[presetId];
    const [tabs, setTabs] = useState(initAllTabs);
    const tab = tabs[presetId];

    /**
     * Review window/level shortcut — a TOGGLE, shared with every other
     * panel that needs one via {@link useReviewWindowLevel}. Both
     * directions flip the display through the SAME event path as a row's
     * window preset menu, so the whole
     * feature stays exactly as transient and untracked as a manual
     * window/level change: nothing is persisted, and every ordinary
     * windowing control still overrides it at any moment.
     *
     * ONE hook instance for the whole panel, never per tab: the display
     * window is a property of the SERIES, not of a tab, so at most one
     * preset can own it at a time. Switching tabs releases it explicitly
     * (see the tab-change handler) before the new tab's preset name
     * takes effect, so the toggle's active state and the current tab can
     * never disagree.
     *
     * Closing the panel while a review window is on leaves the DISPLAY
     * as it is — the hook deliberately does not restore on unmount (see
     * its own doc comment for the stale-series hazard that rules it
     * out), the same as any other transient windowing change, which no
     * panel owns. The user recovers the previous windowing exactly as they
     * would from a manual change: through the viewport windowing
     * controls. `preset.reviewWindowLevel` is absent on the Body tab
     * (no toggle button); the hook treats the `undefined` name as "no
     * review window".
     */
    // Thresholds every voxel of the volume (`bodyContourParams` reads
    // `floatPixelData`, which returns the partially filled buffer rather than
    // refusing). The panel stays open — "still loading" resolves on its own —
    // but the operation cannot run against a volume that is not all there
    // (SR-WOR-H-37-3).
    const imageGate = useImageCompletenessGate(image);

    const reviewWindowLevel = useReviewWindowLevel(
        preset.reviewWindowLevel?.name,
        image
    );
    const patchTab = useCallback(
        (patch: Partial<TabParams>) => {
            setTabs((prev) => ({
                ...prev,
                [presetId]: { ...prev[presetId], ...patch },
            }));
        },
        [presetId]
    );

    const existingExternal = useMemo(
        () => findExternalRoiReadModel(readModel) ?? null,
        [readModel]
    );
    // Which guard, if any, protects the EXTERNAL this preset would replace.
    // Approval and the edit lock both block the write and each names its own
    // remedy, so the panel reports the one that actually applies.
    const externalEditBlocker =
        preset.output.mode === 'single-external'
            ? existingExternal?.contourEditBlocker
            : undefined;

    const isEligible = !!image?.isHuQuantified;

    // A preset may bound its threshold by the image's own maximum (high
    // density): resolved per image, and reported unavailable rather than
    // clamped when the image never reaches the preset's default.
    const thresholdRange = useMemo(
        () => resolveThresholdRange(preset, image?.maxValue ?? 0),
        [preset, image?.maxValue]
    );

    // Optional spatial restriction, edited in the viewports through the
    // shared VOI overlay; shared across the tabs and held for the session
    // (see useVoiRegionRestriction — the on/off flag and the box
    // survive closing and reopening this panel, and reset on an image
    // switch).
    const {
        restrictToRegion,
        handleRestrictToRegionChange,
        resetRegion,
        voiBounds,
    } = useVoiRegionRestriction({
        consumer: 'threshold-segmentation',
        imageUid: image?.dicomUid,
        imageGrid: image?.grid,
    });
    // The restriction box is draggable, so a press that misses a handle
    // would otherwise land on the image as a stroke. Keyed on the
    // restriction being ON, not on the panel being open: the box is
    // optional here, so the panel can be up with nothing to fumble
    // against.
    useParkedContouringTool(restrictToRegion);

    // The keep-inside stage needs a clip target that actually holds
    // geometry; an EXTERNAL without any would clip everything to
    // nothing. Asked of the ENGINE, so a Body this panel generated a
    // moment ago (contours still only in the engine texture, the domain
    // array empty until saveChanges) counts — see
    // roiHasEngineGeometry.
    const clipTargetExternal = useMemo(
        () =>
            existingExternal !== null &&
            roiHasEngineGeometry(
                environment,
                existingExternal.roiNumber,
                existingExternal.contourCount
            )
                ? existingExternal
                : null,
        [environment, existingExternal]
    );
    // The stage is IMPLICIT: no preset exposes it and there is no user
    // flag (same shape as the final fill below). It simply runs on the
    // presets that declare it whenever a usable EXTERNAL exists, and is
    // silently inert when none does.
    const keepInsideActive =
        preset.keepInsideExternal.defaultEnabled && clipTargetExternal !== null;

    // Contour versions of the clip target, kept current: an edit to the
    // EXTERNAL changes the geometry the keep-inside stage consumes, and a
    // held preview must not outlive it.
    const clipTargetRoiNumbers = useMemo(
        () => (clipTargetExternal ? [clipTargetExternal.roiNumber] : []),
        [clipTargetExternal]
    );
    const clipTargetVersions = useRoiSdfVersions(
        environment,
        clipTargetRoiNumbers
    );

    const params: BodyContourParams = useMemo(
        () => ({
            presetId,
            thresholdHu: tab.thresholdHu,
            // The BAND is the preset's declaration, not the tab's: a
            // preset that declares no upper bound classifies at-or-above
            // however its tab state was seeded, so a single-sided preset
            // can never smuggle a ceiling into the compute or the record.
            upperThresholdHu:
                preset.threshold.upper !== undefined
                    ? tab.upperThresholdHu
                    : undefined,
            // Every optional stage routes through stageEnabled: a stage
            // the preset does not expose is locked to its preset default.
            keepLargest2dEnabled: stageEnabled(
                preset.keepLargest2d,
                tab.keepLargest2dEnabled
            ),
            keepLargest2dCount: tab.keepLargest2dCount,
            keepLargest3dEnabled: stageEnabled(
                preset.keepLargest3d,
                tab.keepLargest3dEnabled
            ),
            keepLargest3dCount: tab.keepLargest3dCount,
            minPartSizeEnabled: stageEnabled(
                preset.minPartSize,
                tab.minPartSizeEnabled
            ),
            minPartAreaMm2: tab.minPartAreaMm2,
            fillHoles2dEnabled: stageEnabled(
                preset.fillHoles2d,
                tab.fillHolesEnabled
            ),
            smoothingEnabled: stageEnabled(
                preset.smoothing,
                tab.smoothingEnabled
            ),
            smoothingStrength: tab.smoothingStrength,
            // No preset exposes the final fill — locked to the preset
            // default.
            finalFillHoles2dEnabled: preset.finalFillHoles2d.defaultEnabled,
            marginMm: preset.margin.exposed ? tab.marginMm : 0,
            // The sdfVersion ties the preview key to the clip target's
            // content, kept current by useRoiSdfVersions: an edit to the
            // EXTERNAL marks a held preview outdated when it happens,
            // rather than whenever this panel next re-renders.
            keepInsideExternal:
                keepInsideActive && clipTargetExternal
                    ? {
                          roiNumber: clipTargetExternal.roiNumber,
                          sdfVersion:
                              clipTargetVersions.get(
                                  clipTargetExternal.roiNumber
                              ) ?? 0,
                      }
                    : null,
            boundsMm: restrictToRegion ? voiBounds : null,
        }),
        [
            presetId,
            preset,
            tab,
            restrictToRegion,
            voiBounds,
            keepInsideActive,
            clipTargetExternal,
            clipTargetVersions,
        ]
    );

    // One CPU-mask cache instance for the panel's lifetime, shared by the
    // preview and apply hooks and across tabs — the mask key is
    // preset-agnostic, so a tab switch with identical mask parameters
    // reuses the mask.
    const maskCache = useMemo(() => new BodyContourMaskCache(), []);

    const {
        isPreviewing,
        handlePreview,
        consumePreview,
        recolorPreview,
        abortInFlight: abortPreviewInFlight,
        cleanup: previewCleanup,
        updateView,
        heldKey,
        previewStats,
    } = useBodyContourPreview({ environment, image, maskCache });

    /**
     * Identity every seeded output field is seeded FOR: the structure set
     * it was derived from. Each seed below stores this id rather than a
     * one-shot boolean, and each is TOTAL — it covers every branch of what
     * the set can look like, so the seeded value is a pure function of the
     * set until the user edits it.
     *
     * Both properties matter. A primary-image switch and the structure-set
     * update it causes arrive in EITHER order: a boolean guard re-armed by
     * the image-switch effect could be re-armed AFTER the seeding effects
     * had run and their dependencies had stopped changing, so the seeds
     * never re-ran and an untouched Apply recolored the new set's existing
     * EXTERNAL to the preset default. And a partial seed (one that only
     * wrote when an EXTERNAL existed) needed the image-switch effect to
     * reset the other branch, which is the same race one level down.
     * Keyed on the set and total, the seeds need no reset to race with.
     */
    const seedKey = readModel?.id ?? null;
    // Seeding guards: the structure set each field was last seeded for
    // (see the seeding effects below).
    const outputSeededKeyRef = useRef<StructureSetId | null>(null);
    const nameSeededKeyRef = useRef<StructureSetId | null>(null);
    const colorSeededKeyRef = useRef<StructureSetId | null>(null);

    // Reset on primary-image change: a stale clinical threshold must not
    // carry across scans, and the held preview and cached mask belong to
    // the previous scan. The region box is NOT reset here — the
    // restriction record is stamped with the image uid, so it resets for
    // the new scan whether or not this panel happens to be open.
    //
    // The OUTPUT fields (by-name targets, the Body name and color) are
    // deliberately not reset here. They are seeded per structure set by
    // the effects below, which re-derive them as soon as the new scan's
    // set arrives — in whichever order the two updates land. Resetting
    // them here as well only raced those seeds: a set update that landed
    // BEFORE the image switch was seeded correctly and then overwritten
    // by this reset, with nothing left to trigger a reseed. The
    // roiNumber-recycling hazard the reset guarded against (roiNumbers
    // restart at 1 in every set, so a held outputRoiNumber would re-bind
    // to an unrelated structure) is covered by the seeds being keyed on
    // the set id: a different set always re-resolves the target.
    const imageUidRef = useRef(image?.dicomUid);
    useEffect(() => {
        if (image?.dicomUid !== imageUidRef.current) {
            imageUidRef.current = image?.dicomUid;
            setTabs((prev) =>
                mapPresets((p) => ({
                    ...prev[p.id],
                    thresholdHu: p.threshold.defaultHu,
                    // The band's ceiling is as clinical as its floor and
                    // resets with it.
                    upperThresholdHu: p.threshold.upper?.defaultHu,
                }))
            );
            previewCleanup();
            maskCache.clear();
            // The captured pre-review windowing (if any) belongs to the
            // series that just went away — {@link useReviewWindowLevel}
            // drops it itself, without publishing, when its own
            // `seriesUid` argument changes; nothing to do here.
        }
    }, [image?.dicomUid, previewCleanup, maskCache]);

    const cleanupRef = useRef(previewCleanup);
    cleanupRef.current = previewCleanup;

    const handleClose = useCallback(() => {
        previewCleanup();
        onClose();
    }, [previewCleanup, onClose]);

    const apply = useBodyContourApplication({
        environment,
        image,
        onSuccess: handleClose,
        updateView,
        consumePreview,
        maskCache,
    });

    // One busy flag for both concerns — close prevention and button
    // gating — so they can never silently diverge.
    const isBusy = isPreviewing || apply.isProcessing;
    useReportBusy(isBusy, onPreventCloseChange);

    const existingNames = useMemo(
        () => readModel?.roiArray.map((r) => r.name) ?? [],
        [readModel]
    );
    // Prefill the Body name as a VALUE, not a placeholder: the committed
    // name must be exactly what the field shows. Seeded once per structure
    // set, and only on the create path — with an EXTERNAL present there is
    // no name field, and not marking the seed done leaves it to seed if
    // that EXTERNAL is deleted while the panel stays open. A later edit
    // (including clearing the field, a validation ERROR here) is the
    // user's and is never overwritten.
    useEffect(() => {
        if (
            !seedKey ||
            nameSeededKeyRef.current === seedKey ||
            existingExternal
        ) {
            return;
        }
        nameSeededKeyRef.current = seedKey;
        setTabs((prev) => ({
            ...prev,
            body: {
                ...prev.body,
                customRoiName: generateUniqueStructureNameFromList(
                    existingNames,
                    THRESHOLD_SEGMENTATION_PRESETS.body.output.name
                ),
            },
        }));
    }, [seedKey, existingExternal, existingNames]);
    // Seed the Body swatch from the structure set: the existing EXTERNAL's
    // current color, so an untouched Apply preserves it, or the preset
    // default when the set holds none. Seeded once per structure set; a
    // later pick is the user's. TOTAL on purpose — a seed that only wrote
    // the EXTERNAL branch would leave the PREVIOUS set's EXTERNAL color in
    // the swatch when the new set has none, and an untouched Apply would
    // then create the new EXTERNAL in a color the user never picked.
    useEffect(() => {
        if (!seedKey || colorSeededKeyRef.current === seedKey) {
            return;
        }
        colorSeededKeyRef.current = seedKey;
        const seededColor = existingExternal
            ? { ...existingExternal.color }
            : { ...THRESHOLD_SEGMENTATION_PRESETS.body.output.color };
        setTabs((prev) => ({
            ...prev,
            body: { ...prev.body, color: seededColor },
        }));
    }, [seedKey, existingExternal]);

    /**
     * Result-target options for the by-name presets, in the shape the
     * shared {@link OutputStructureSelect} consumes.
     * Approved structures stay in the list — the select is the single
     * place that filters them out, and it also drops a selection that
     * becomes approved while the panel is open. Point ROIs are removed
     * here: they carry no contour geometry, so they can never receive an
     * operation result.
     */
    const availableStructures: StructureOption[] = useMemo(
        () =>
            readModel?.roiArray
                .filter((r) => !r.isPointRoi)
                .map((r) => ({
                    name: r.name,
                    roiNumber: r.roiNumber,
                    color: r.color,
                    isWriteProtected: !r.canModifyContours,
                })) ?? [],
        [readModel]
    );

    /**
     * Result-target options for the Body preset's output select: the
     * domain's single-EXTERNAL rule leaves exactly one valid existing
     * target — the EXTERNAL itself — so nothing else is offered (writing
     * an EXTERNAL result into a structure of another type would retype
     * it, which the domain forbids). Empty when the set holds none: the
     * select then offers only its New Structure entry, in parallel with
     * the by-name presets' create path. An APPROVED EXTERNAL stays the
     * one option — rendered locked by the select — so the panel still
     * shows what the operation is bound to while Apply is blocked with
     * the reason (see externalEditBlocker).
     */
    const bodyOutputOptions: StructureOption[] = useMemo(
        () =>
            existingExternal && readModel
                ? [
                      {
                          name: existingExternal.name,
                          roiNumber: existingExternal.roiNumber,
                          color: existingExternal.color,
                          isWriteProtected: !existingExternal.canModifyContours,
                      },
                  ]
                : [],
        [existingExternal, readModel]
    );

    // Seed the by-name presets' output target once the structure set is
    // known: a structure already carrying the preset's name is the
    // preselected target (re-applying replaces what this tool created
    // earlier). An approved one is not selectable at all — the select
    // excludes approved structures — so it falls back to New Structure
    // with the preset's default name made unique, exactly like the
    // no-match case. Seeded once PER STRUCTURE SET, keyed by the set's
    // id: when the set changes (a structure-set switch, or the set update
    // that follows an image switch) the target is re-resolved against the
    // NEW set — never carried over by a roiNumber the new set recycles
    // for an unrelated structure. Within one set, every later choice is
    // the user's.
    useEffect(() => {
        if (!readModel || !seedKey || outputSeededKeyRef.current === seedKey) {
            return;
        }
        outputSeededKeyRef.current = seedKey;
        setTabs((prev) => {
            const next = { ...prev };
            for (const id of THRESHOLD_SEGMENTATION_PRESET_ORDER) {
                const p = THRESHOLD_SEGMENTATION_PRESETS[id];
                if (p.output.mode !== 'overwrite-own-by-name') {
                    continue;
                }
                const resolution = resolveOwnOutputByName(
                    readModel.roiArray,
                    p.output.name
                );
                next[id] =
                    resolution.kind === 'overwrite'
                        ? {
                              ...prev[id],
                              outputTarget: overwriteTarget(
                                  resolution.roiNumber
                              ),
                          }
                        : {
                              ...prev[id],
                              outputTarget: NEW_OUTPUT_TARGET,
                              customRoiName:
                                  generateUniqueStructureNameFromList(
                                      existingNames,
                                      p.output.name
                                  ),
                          };
            }
            return next;
        });
    }, [seedKey, readModel, existingNames]);

    const setOutputTarget = useCallback(
        (outputTarget: OutputTarget) => patchTab({ outputTarget }),
        [patchTab]
    );

    // Output resolution. Body (single-external): the existing EXTERNAL is
    // the fixed output, else a new uniquely-named EXTERNAL. Bone / High
    // density (overwrite-own-by-name): an existing structure picked in
    // the output select, or — on the New Structure path — resolved
    // against the typed name, so a name already held by an unapproved
    // structure still replaces it and an approved one still refuses.
    const ownResolution = useMemo(
        () =>
            preset.output.mode === 'overwrite-own-by-name' && readModel
                ? resolveOwnOutputByName(
                      readModel.roiArray,
                      tab.customRoiName.trim()
                  )
                : null,
        [preset, readModel, tab.customRoiName]
    );

    const customNameError = useMemo(() => {
        if (preset.output.mode === 'single-external') {
            if (existingExternal) {
                return null;
            }
            // The domain validator, not margin's getCustomNameError: an
            // empty field must surface as an error and disable Apply.
            const [valid, error] = validateStructureName(
                tab.customRoiName,
                existingNames
            );
            return valid ? null : error;
        }
        // Overwrite was chosen in the output select: there is no name
        // field to validate, and the select never offers an approved
        // target.
        if (tab.outputTarget.kind === 'existing') {
            return null;
        }
        // By-name output: a duplicate name is the OVERWRITE path, not an
        // error — validate syntax only; approval is surfaced separately.
        const [valid, error] = validateStructureName(tab.customRoiName, []);
        if (!valid) {
            return error;
        }
        return ownResolution ? ownOutputRefusal(intl, ownResolution) : null;
    }, [
        preset,
        existingExternal,
        tab.customRoiName,
        tab.outputTarget,
        existingNames,
        ownResolution,
        intl,
    ]);

    const currentPreviewKey = useMemo(
        () => (image ? bodyContourPreviewKey(image, params) : null),
        [image, params]
    );

    // Whether an automatic run has already been STARTED for a key, so a
    // re-render cannot restart one, and a settled outcome — including an
    // empty result or a failure — is not retried forever.
    const attemptedKeyRef = useRef<string | null>(null);
    // Bumped when a run is deferred because another operation owns the
    // GPU, to re-offer it without narrating the contention.
    const [previewRetry, setPreviewRetry] = useState(0);
    // The preview inputs as they stand, read at fire time rather than
    // depended on: the effect keys on the preview KEY, and params/color
    // are new objects on every render.
    const previewInputsRef = useRef({ params, color: tab.color, preset });
    previewInputsRef.current = { params, color: tab.color, preset };

    // The preview is AUTOMATIC: the viewports show the result of the
    // parameters as they stand, so what is on screen is always what Apply
    // would commit and there is nothing to mark outdated. A change aborts
    // the run in flight (usePreviewBase keeps the displayed preview up
    // until its replacement is ready, so there is no blank frame) and
    // schedules a new one; the delay coalesces a slider drag or a held
    // arrow key into a single compute.
    useEffect(() => {
        if (currentPreviewKey === null) {
            return;
        }
        if (attemptedKeyRef.current === currentPreviewKey) {
            return;
        }
        abortPreviewInFlight();
        const timer = setTimeout(() => {
            const inputs = previewInputsRef.current;
            void handlePreview(inputs.params, inputs.color, {
                // Whether the displayed preview recedes the other
                // structures is the preset's call: the recede RE-TINTS
                // them (the contour shader folds the alpha into RGB),
                // which is right when the preview sits on top of the
                // structure it replaces (Body) and a display defect when
                // it does not — a High density preview must not repaint
                // the patient outline it is clipped to. So is whether the
                // reported statistics carry a component count: only High
                // density's output is normally N separate objects, and
                // only it displays the count.
                dimsOtherStructures: inputs.preset.previewDimsOtherStructures,
                reportComponents: inputs.preset.reportComponents === true,
            }).then((settled) => {
                if (settled) {
                    attemptedKeyRef.current = currentPreviewKey;
                } else {
                    setPreviewRetry((n) => n + 1);
                }
            });
        }, WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [currentPreviewKey, previewRetry, abortPreviewInFlight, handlePreview]);

    // A TAB SWITCH is different from a parameter change: the held
    // preview is the previous preset's structure entirely — a different
    // output, type and color — so it is CLEARED (in-flight compute
    // aborted, held texture disposed, overlay removed), not merely
    // marked outdated. Leaving another preset's outline on screen under
    // this tab's controls presents it as this tab's result.
    // cleanupRef, not previewCleanup: this effect keys on the preset
    // alone, and the callback's identity changes with the environment.
    // Its first run (mount) is a no-op: nothing is held yet.
    useEffect(() => {
        cleanupRef.current();
        attemptedKeyRef.current = null;
    }, [presetId]);

    // Whether the held preview is the CURRENT parameters' result. Under
    // an automatic preview this is a transient state — the replacement is
    // already scheduled — so it gates the repaint below rather than
    // surfacing to the user as a staleness warning.
    const isPreviewCurrent = heldKey !== null && heldKey === currentPreviewKey;

    // The on-screen preview follows the chosen color immediately — a
    // repaint of the held texture, never a recompute (color is
    // deliberately outside the preview key). Only while the held preview
    // is the CURRENT parameters' result: repainting the outgoing one
    // during a recompute would present the previous parameters' geometry
    // as this color's structure.
    useEffect(() => {
        if (isPreviewCurrent) {
            recolorPreview(tab.color);
        }
    }, [tab.color, isPreviewCurrent, recolorPreview]);

    /**
     * Switching preset (tab) releases the review window, exactly as
     * pressing the toggle off would — {@link useReviewWindowLevel}'s
     * `release` is already a no-op while inactive, so this needs no
     * guard of its own.
     *
     * The regression this fixes: the review window is a TAB-scoped
     * transient — its button lives on the threshold row of the tab that
     * applied it — but the display it changed is not. Leaving the tab
     * used to leave the review window on screen with no pressed button
     * anywhere (and none at all on the Body tab), so the only way back
     * to the user's own windowing was to redo it by hand. Tearing it
     * down on leave mirrors what the held preview already does on a
     * preset switch: transient state a tab owns is dismantled when the
     * user leaves it, never abandoned on screen.
     */
    const handlePresetChange = useCallback(
        (next: ThresholdSegmentationPresetId) => {
            reviewWindowLevel.release();
            setPresetId(next);
        },
        [reviewWindowLevel]
    );

    const handleApply = useCallback(() => {
        if (preset.output.mode === 'single-external') {
            if (existingExternal && !externalEditBlocker) {
                apply.handleApply(
                    params,
                    preset,
                    existingExternal.roiNumber,
                    tab.color
                );
                return;
            }
            apply.handleApply(
                params,
                preset,
                null,
                tab.color,
                generateUniqueStructureNameFromList(
                    existingNames,
                    tab.customRoiName.trim()
                )
            );
            return;
        }
        if (tab.outputTarget.kind === 'existing') {
            // An existing structure was chosen in the output select. It
            // can never be an approved one — the select excludes them and
            // drops a target that becomes approved — and the apply hook's
            // destination resolution refuses a non-modifiable ROI anyway.
            const { roiNumber } = tab.outputTarget;
            if (roiNumber !== null) {
                apply.handleApply(params, preset, roiNumber, tab.color);
            }
            return;
        }
        // Narrowing guard only: ownResolution is non-null whenever the
        // preset resolves by name and a structure set is loaded, and the
        // Apply button renders only behind the !readModel guard.
        if (!ownResolution) {
            return;
        }
        // Defense in depth (the Apply button is already disabled): an
        // approved structure, or one of several sharing the name, is never
        // overwritten by re-apply.
        const refusal = ownOutputRefusal(intl, ownResolution);
        if (refusal) {
            enqueueSnackbar(refusal, { variant: 'error' });
            return;
        }
        apply.handleApply(
            params,
            preset,
            ownResolution.kind === 'overwrite' ? ownResolution.roiNumber : null,
            tab.color,
            ownResolution.kind === 'create'
                ? tab.customRoiName.trim()
                : undefined
        );
    }, [
        apply,
        params,
        preset,
        existingExternal,
        externalEditBlocker,
        existingNames,
        ownResolution,
        tab.color,
        tab.customRoiName,
        tab.outputTarget,
        intl,
    ]);

    // The tab strip is rendered by BOTH the 'unavailable' branch below and
    // the main return, so it is built once here. A preset that cannot run
    // on this image must still leave the user a way OUT of it: without the
    // strip, the High density tab on an image that never reaches its
    // threshold is a dead end with no route back to Body or Bone.
    // Each tab carries its preset's description as a tooltip — the panel
    // body is for this preset's controls, so what the OTHER presets do is
    // told on demand rather than pushed into the layout.
    const tabStrip = (
        <Tabs
            value={presetId}
            onChange={(_, value) =>
                handlePresetChange(value as ThresholdSegmentationPresetId)
            }
            variant="fullWidth"
            indicatorColor="primary"
            textColor="inherit"
            sx={{
                minHeight: 32,
                '& .MuiTab-root': {
                    minHeight: 32,
                    py: 0.5,
                    fontSize: 12,
                    textTransform: 'none',
                },
            }}
        >
            {THRESHOLD_SEGMENTATION_PRESET_ORDER.map((id) => (
                // value on the Tooltip as well as the Tab: Tabs reads
                // `child.props.value` off its DIRECT child to map the
                // selection, and the Tooltip is that child. It forwards
                // everything Tabs clones onto it (selected, indicator,
                // onChange) down to the Tab, whose own props win.
                <PresetTabTooltip
                    key={id}
                    value={id}
                    title={intl.formatMessage({
                        id: THRESHOLD_SEGMENTATION_PRESETS[id].descriptionId,
                    })}
                    placement="bottom"
                    // describeChild: the description DESCRIBES the tab, it
                    // does not name it. Without this MUI would publish it
                    // as the tab's aria-label and a screen-reader user
                    // would lose the short name the tab strip shows.
                    describeChild
                >
                    <Tab
                        value={id}
                        label={intl.formatMessage({
                            id: THRESHOLD_SEGMENTATION_PRESETS[id].tabLabelId,
                        })}
                        data-testid={`threshold-seg-tab-${id}`}
                    />
                </PresetTabTooltip>
            ))}
        </Tabs>
    );

    if (!readModel) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="thresholdSeg.noStructureSet" />
            </Typography>
        );
    }
    if (!isEligible) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="thresholdSeg.notCt" />
            </Typography>
        );
    }
    if (thresholdRange.kind === 'unavailable') {
        // The image never reaches this preset's threshold, so every
        // parameter below would operate on an empty mask. Refuse with the
        // reason rather than offer a degenerate slider — but keep the tab
        // strip: the refusal is about THIS preset, and the other presets
        // are still runnable, so this must not trap the user on the tab.
        return (
            <Stack spacing={1}>
                {tabStrip}
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                        id="thresholdSeg.thresholdUnavailable"
                        values={{
                            defaultHu: thresholdRange.defaultHu,
                            imageMaxHu: thresholdRange.imageMaxHu,
                        }}
                    />
                </Typography>
            </Stack>
        );
    }

    const isBodyCreatePath =
        preset.output.mode === 'single-external' && !existingExternal;
    const isCustomNameInvalid =
        (isBodyCreatePath || preset.output.mode === 'overwrite-own-by-name') &&
        !!customNameError;
    const showsFixedOutput =
        preset.output.mode === 'single-external' && existingExternal !== null;
    const showsOutputSelect = preset.output.mode === 'overwrite-own-by-name';
    const targetBlocker = showsOutputSelect
        ? outputTargetBlocker(intl, tab.outputTarget)
        : undefined;
    // The structure this Apply would replace, whichever way the by-name
    // output resolved: the target picked in the select, or a typed name
    // that an unapproved structure already holds.
    const overwriteTargetName = showsOutputSelect
        ? tab.outputTarget.kind === 'existing'
            ? (availableStructures.find(
                  (s) => s.roiNumber === overwrittenRoiNumber(tab.outputTarget)
              )?.name ?? null)
            : ownResolution?.kind === 'overwrite'
              ? ownResolution.name
              : null
        : null;

    // A BANDED preset (Bone) classifies a voxel in the structure iff
    // lower ≤ HU ≤ upper, and edits both bounds with one two-handle
    // slider; the single-sided presets (Body, High density) keep the
    // one-handle control and stay unbounded above. The registry declares
    // the band and the tab holds its ceiling, so both must be present —
    // the band is never inferred from tab state alone.
    const bandUpper = preset.threshold.upper;
    const upperThresholdHu = tab.upperThresholdHu;
    const isBanded = bandUpper !== undefined && upperThresholdHu !== undefined;

    // Tooltip and accessible name of the review-W/L toggle. Two
    // strings, not one: what the press will DO differs by direction, and
    // the active wording is where the restore promise is spelled out.
    const reviewWindowLevelMessageId = reviewWindowLevel.isActive
        ? 'thresholdSeg.reviewWindowLevelActiveTooltip'
        : 'thresholdSeg.reviewWindowLevelTooltip';

    return (
        // One spacing unit for every row (nested Stacks, no per-row
        // mt/mb), so the panel keeps a regular vertical rhythm as rows
        // appear and disappear per preset.
        <Stack spacing={1}>
            {tabStrip}

            <Box>
                {/* The review-W/L toggle shares the threshold label's
                    row: it is about reviewing THIS threshold's result on
                    screen, so it belongs beside the value it reviews
                    rather than in a row of its own. It renders only for
                    the presets that declare a review window (Bone / High
                    density); on the Body tab the row is the bare label,
                    identical to before.

                    The button is padded down (p 0.25 → 18 px tall)
                    deliberately: the row's height must stay the label's
                    own, or the negative top margin of the slider below
                    (THRESHOLD_SLIDER_SX, tuned against the bare label)
                    would no longer pull it flush and the label would
                    detach from its control. */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Label>
                        <FormattedMessage id="thresholdSeg.threshold" />
                        {isBanded
                            ? `: ${tab.thresholdHu}–${upperThresholdHu} HU`
                            : `: ${tab.thresholdHu} HU`}
                    </Label>
                    {preset.reviewWindowLevel && (
                        <Tooltip
                            title={
                                <FormattedMessage
                                    id={reviewWindowLevelMessageId}
                                    values={{
                                        ww: preset.reviewWindowLevel.ww,
                                        wc: preset.reviewWindowLevel.wc,
                                    }}
                                />
                            }
                            placement="left"
                        >
                            <IconButton
                                size="small"
                                onClick={reviewWindowLevel.toggle}
                                aria-label={intl.formatMessage(
                                    { id: reviewWindowLevelMessageId },
                                    {
                                        ww: preset.reviewWindowLevel.ww,
                                        wc: preset.reviewWindowLevel.wc,
                                    }
                                )}
                                // The pressed state is the promise that
                                // the review window is on screen AND
                                // that a second press restores what was
                                // there before — kept true by the
                                // hook's own foreign-change subscription.
                                aria-pressed={reviewWindowLevel.isActive}
                                sx={(theme) => ({
                                    p: 0.25,
                                    ...(reviewWindowLevel.isActive
                                        ? theme.mixins.pressedIconButton
                                        : {}),
                                })}
                                data-testid="threshold-seg-review-wl"
                            >
                                <Contrast size={14} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {/* The 'unavailable' case returned above, so the resolved
                    range is the only one that can reach either slider.

                    The two variants differ in their TRACK on purpose: the
                    filled part of the track must always be the INCLUDED
                    part of the HU axis. A banded preset includes exactly
                    what lies between its handles, which is the default
                    (normal) range track. A single-sided preset includes
                    everything AT OR ABOVE its one handle, which is
                    track="inverted" (thumb→max) — the default min→thumb
                    fill would highlight the excluded side. The note
                    beneath states the same in words with the live values;
                    for the single-sided presets the slider max is only
                    the adjustable range, never an inclusion bound, so
                    that note names "and above" rather than the range end.

                    The banded slider spans the LOWER handle's minimum to
                    the UPPER handle's maximum with disableSwap, so the
                    handles cannot cross. A degenerate near-empty band is
                    reachable and deliberately allowed: the empty-result
                    refusal surfaces it with its reason, which is this
                    project's way — never a silently clamped clinical
                    value. */}
                {isBanded ? (
                    <Slider
                        size="small"
                        value={[tab.thresholdHu, upperThresholdHu]}
                        min={thresholdRange.minHu}
                        max={bandUpper.maxHu}
                        step={1}
                        disableSwap
                        onChange={(_, value) => {
                            const [lower, upper] = value as number[];
                            patchTab({
                                thresholdHu: lower,
                                upperThresholdHu: upper,
                            });
                        }}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v} HU`}
                        getAriaLabel={(index) =>
                            intl.formatMessage({
                                id:
                                    index === 0
                                        ? 'thresholdSeg.thresholdLowerAria'
                                        : 'thresholdSeg.thresholdUpperAria',
                            })
                        }
                        sx={THRESHOLD_SLIDER_SX}
                    />
                ) : (
                    <Slider
                        size="small"
                        track="inverted"
                        value={tab.thresholdHu}
                        min={thresholdRange.minHu}
                        max={thresholdRange.maxHu}
                        step={1}
                        onChange={(_, value) =>
                            patchTab({ thresholdHu: value as number })
                        }
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v} HU`}
                        aria-label={intl.formatMessage({
                            id: 'thresholdSeg.thresholdAria',
                        })}
                        sx={THRESHOLD_SLIDER_SX}
                    />
                )}
                <Typography fontSize={10} fontStyle="italic">
                    {isBanded ? (
                        <FormattedMessage
                            id="thresholdSeg.thresholdIncludesRangeNote"
                            values={{
                                lowerHu: tab.thresholdHu,
                                upperHu: upperThresholdHu,
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="thresholdSeg.thresholdIncludesNote"
                            values={{ thresholdHu: tab.thresholdHu }}
                        />
                    )}
                </Typography>
            </Box>

            {/* Optional spatial restriction: enabling shows the shared VOI
                box on the viewports, seeded to the centred half of the
                image extent — so enabling it DOES change the result, and
                the box is visibly a restriction. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <CompactToggle
                    labelId="thresholdSeg.restrictRegion"
                    tooltipId="thresholdSeg.restrictRegionTooltip"
                    value={restrictToRegion}
                    onChange={handleRestrictToRegionChange}
                    dataTestId="threshold-seg-restrict-region"
                    tooltipPlacement={STAGE_ROW_TOOLTIP_PLACEMENT}
                />
                {/* Only while there is a region to reset — off, the seed
                        has not run and the button would act on nothing. */}
                {restrictToRegion && (
                    <RegionResetButton onReset={resetRegion} />
                )}
            </Box>

            {/* Cleanup stages in pipeline order: fill holes → smoothing →
                min-size → keep 2D → keep 3D → margin → keep inside
                EXTERNAL. Rows render only when the preset exposes the
                stage — a hidden stage is locked to its preset default
                (see stageEnabled). */}
            {preset.fillHoles2d.exposed && (
                <Box>
                    <CompactToggle
                        labelId="thresholdSeg.fillHoles"
                        tooltipId="thresholdSeg.fillHolesTooltip"
                        value={tab.fillHolesEnabled}
                        onChange={(v) => patchTab({ fillHolesEnabled: v })}
                        tooltipPlacement={STAGE_ROW_TOOLTIP_PLACEMENT}
                    />
                </Box>
            )}

            {preset.smoothing.exposed && (
                <SmoothingToggleRow
                    labelId="thresholdSeg.smoothing"
                    tooltipId="thresholdSeg.smoothingTooltip"
                    enabled={tab.smoothingEnabled}
                    onEnabledChange={(v) => patchTab({ smoothingEnabled: v })}
                    lightLabelId="thresholdSeg.smoothing.light"
                    strongLabelId="thresholdSeg.smoothing.strong"
                    strength={tab.smoothingStrength}
                    onStrengthChange={(v) => patchTab({ smoothingStrength: v })}
                />
            )}

            {/* cm² at the UI boundary, mm² in the domain (the ui_specs
                rule the margin row below follows): the stored TabParams /
                preset bounds stay mm² and are converted only here — the
                shared row renders display units only. The floor is a
                per-slice in-plane area, displayed as cm². */}
            {preset.minPartSize.exposed && (
                <ToggleNumberRow
                    labelId="thresholdSeg.minSize"
                    tooltipId="thresholdSeg.minSizeTooltip"
                    enabled={tab.minPartSizeEnabled}
                    onEnabledChange={(v) => patchTab({ minPartSizeEnabled: v })}
                    value={mm2ToCm2(tab.minPartAreaMm2)}
                    onValueChange={(v) =>
                        patchTab({ minPartAreaMm2: cm2ToMm2(v) })
                    }
                    min={mm2ToCm2(preset.minPartSize.minAreaMm2)}
                    step={0.5}
                    precision={1}
                    unit="cm²"
                    dataTestId="threshold-seg-min-area"
                />
            )}

            {preset.keepLargest2d.exposed && (
                <ToggleNumberRow
                    labelId="thresholdSeg.keepLargest2d"
                    tooltipId="thresholdSeg.keepLargest2dTooltip"
                    enabled={tab.keepLargest2dEnabled}
                    onEnabledChange={(v) =>
                        patchTab({ keepLargest2dEnabled: v })
                    }
                    value={tab.keepLargest2dCount}
                    onValueChange={(v) => patchTab({ keepLargest2dCount: v })}
                    min={1}
                    step={1}
                    precision={0}
                    snapToStep
                    dataTestId="threshold-seg-keep-largest-2d-count"
                />
            )}

            {preset.keepLargest3d.exposed && (
                <ToggleNumberRow
                    labelId="thresholdSeg.keepLargest3d"
                    tooltipId="thresholdSeg.keepLargest3dTooltip"
                    enabled={tab.keepLargest3dEnabled}
                    onEnabledChange={(v) =>
                        patchTab({ keepLargest3dEnabled: v })
                    }
                    value={tab.keepLargest3dCount}
                    onValueChange={(v) => patchTab({ keepLargest3dCount: v })}
                    min={1}
                    step={1}
                    precision={0}
                    snapToStep
                    dataTestId="threshold-seg-keep-largest-3d-count"
                />
            )}

            {/* cm at the UI boundary, mm in the domain (the ui_specs rule
                the margin panel follows): the stored TabParams / preset
                bounds stay mm and are converted only here. step 0.05 cm =
                0.5 mm granularity. */}
            {preset.margin.exposed && (
                <ValueRow
                    labelId="thresholdSeg.margin"
                    value={mmToCm(tab.marginMm)}
                    onValueChange={(v) => patchTab({ marginMm: cmToMm(v) })}
                    min={mmToCm(preset.margin.minMm)}
                    max={mmToCm(preset.margin.maxMm)}
                    step={0.05}
                    snapToStep
                    precision={2}
                    unit="cm"
                    dataTestId="threshold-seg-margin-cm"
                />
            )}

            {/* The keep-inside-EXTERNAL stage has no row: it is implicit
                (see keepInsideActive above) — it runs after the margin
                and before the region clamp whenever a usable EXTERNAL
                exists, and is inert when none does. */}

            {/* Output block, set off from the parameter rows above by one
                extra spacing unit (padding, so the Stack rhythm below it
                stays uniform). */}
            <Stack spacing={1} sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontSize={12} color="text.secondary">
                        <FormattedMessage id="thresholdSeg.color" />
                    </Typography>
                    <ColorSwatchPicker
                        color={tab.color}
                        onChange={(c) => patchTab({ color: c })}
                        ariaLabel={intl.formatMessage({
                            id: 'thresholdSeg.color',
                        })}
                        dataTestId="threshold-seg-color-swatch"
                    />
                </Box>

                {showsFixedOutput && !externalEditBlocker && (
                    <Typography fontSize={10} fontStyle="italic">
                        <FormattedMessage
                            id="thresholdSeg.replacesExisting"
                            values={{ name: existingExternal!.name }}
                        />
                    </Typography>
                )}

                {externalEditBlocker && (
                    <Typography fontSize={10} color="error" fontStyle="italic">
                        <FormattedMessage
                            id={
                                externalEditBlocker === 'lock'
                                    ? 'thresholdSeg.lockedExternal'
                                    : 'thresholdSeg.approvedExternal'
                            }
                            values={{ name: existingExternal!.name }}
                        />
                    </Typography>
                )}

                {overwriteTargetName !== null && (
                    <Typography fontSize={10} fontStyle="italic">
                        <FormattedMessage
                            id="thresholdSeg.replacesExisting"
                            values={{ name: overwriteTargetName }}
                        />
                    </Typography>
                )}

                {preset.output.mode === 'single-external' ? (
                    // Body: the same house output control as the other
                    // presets, constrained by the domain's
                    // single-EXTERNAL rule — the existing EXTERNAL is
                    // the ONLY offered target, with no New Structure
                    // entry (a second EXTERNAL is domain-forbidden);
                    // with none, New Structure is the sole entry and the
                    // name field renders below, exactly parallel to the
                    // by-name presets' create path. The value is DERIVED
                    // from the read model, never stored — see
                    // NOOP_SET_BODY_OUTPUT — so an image or structure-
                    // set switch re-resolves it by construction.
                    <OutputStructureSelect
                        label={intl.formatMessage({
                            id: 'thresholdSeg.outputNameLabel',
                        })}
                        target={
                            existingExternal
                                ? overwriteTarget(existingExternal.roiNumber)
                                : NEW_OUTPUT_TARGET
                        }
                        onTargetChange={NOOP_SET_BODY_OUTPUT}
                        inputColor={getStructureColor(tab.color)}
                        inputRoiNumber={NO_INPUT_STRUCTURE}
                        availableStructures={bodyOutputOptions}
                        allowNew={existingExternal === null}
                        customRoiName={tab.customRoiName}
                        setCustomRoiName={(v) => patchTab({ customRoiName: v })}
                        customNameError={customNameError}
                        defaultName={preset.output.name}
                        dataTestId="threshold-seg-output-select"
                    />
                ) : (
                    // Bone / High density: the house output control
                    // (margin, ring) — "New Structure" plus every
                    // eligible existing structure, with its own name
                    // field on the New Structure path. There is no input
                    // structure to offer as "(Current)" — this operation
                    // reads the IMAGE — so the input roiNumber is a
                    // sentinel that matches nothing.
                    <OutputStructureSelect
                        label={intl.formatMessage({
                            id: 'thresholdSeg.outputNameLabel',
                        })}
                        target={tab.outputTarget}
                        onTargetChange={setOutputTarget}
                        inputColor={getStructureColor(tab.color)}
                        inputRoiNumber={NO_INPUT_STRUCTURE}
                        availableStructures={availableStructures}
                        customRoiName={tab.customRoiName}
                        setCustomRoiName={(v) => patchTab({ customRoiName: v })}
                        customNameError={customNameError}
                        defaultName={preset.output.name}
                        dataTestId="threshold-seg-output-select"
                    />
                )}
            </Stack>

            {isPreviewing && (
                <Typography fontSize={10} fontStyle="italic">
                    <FormattedMessage id="thresholdSeg.previewing" />
                </Typography>
            )}
            {/* What the preview actually contains. The reason it exists:
                a preview can be correct and still look like nothing on
                screen — High density's normal output is a few scattered
                fragments, and a threshold one step too high shows the
                same "almost nothing" as a threshold that found the
                implants. A volume the user can read makes the difference
                checkable without hunting through slices, and on the High
                density tab the component count answers the question its
                output actually raises ("did it catch all three markers?").

                It reports the HELD preview — the outline on screen — so
                it survives the preview going stale (the warning above
                already says the parameters have moved on) and disappears
                with the preview itself. Suppressed while a new preview
                computes, exactly like the stale warning: during that
                window the panel gives no outdatedness cue, and a number
                left standing there would read as the incoming result's.

                mm³ → cm³ at this boundary, unit in the label. */}
            {!isPreviewing && previewStats !== null && (
                <Typography
                    fontSize={10}
                    fontStyle="italic"
                    data-testid="threshold-seg-preview-stats"
                >
                    {previewStats.componentCount !== undefined ? (
                        <FormattedMessage
                            id="thresholdSeg.previewVolumeComponents"
                            values={{
                                volume: formatMm3AsCm3(previewStats.volumeMm3),
                                count: previewStats.componentCount,
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="thresholdSeg.previewVolume"
                            values={{
                                volume: formatMm3AsCm3(previewStats.volumeMm3),
                            }}
                        />
                    )}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    {/* Apply is gated while a preview computes: applying
                        mid-preview started a second un-cancellable
                        full-volume compute and could race the GL guard
                        into a spurious "GPU unavailable" failure. */}
                    <ActionButtons
                        label={intl.formatMessage({ id: 'thresholdSeg.apply' })}
                        onApply={handleApply}
                        isApplyDisabled={
                            externalEditBlocker !== undefined ||
                            isCustomNameInvalid ||
                            targetBlocker !== undefined ||
                            isBusy ||
                            imageGate.isBlocked
                        }
                        applyDisabledReason={targetBlocker ?? imageGate.reason}
                    />
                </Box>
            </Box>
        </Stack>
    );
}
